'use client';

import { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { ImageParameters, HistoryEntry } from '@/lib/mcp/types';
import { DEFAULT_PARAMS } from '@/lib/mcp/constants/imageGeneration';
import { usePromptManagement } from '@/lib/mcp/hooks/usePromptManagement';
// import { getImageHistory, generateImage, restoreVersion, getParameters, deleteHistoryVersion } from '@/lib/mcp/api/mcpClient';
import { getImageHistory, generateImage, getParameters, deleteHistoryVersion } from '@/lib/mcp/api/mcpClient';
import { Trash2 } from 'lucide-react';
import Image from 'next/image';
// fs is not usable in the browser

interface UnifiedDebugPanelProps {
  imageName: string;
  // serviceUrl is not used, can be removed if not needed for future features
  serviceUrl: string;
  defaultTab?: string;
  onRefresh?: () => void;
}

export function UnifiedDebugPanel({ 
  imageName, 
  serviceUrl,
  defaultTab = 'generate',
  onRefresh
}: UnifiedDebugPanelProps) {
  const { toast } = useToast();
  
  // Basic state management
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [generationProgress, setGenerationProgress] = useState<number>(0);
  
  // Image generation parameters
  const [generationType, setGenerationType] = useState<'hero' | 'image'>('hero');
  const [outputFileName, setOutputFileName] = useState<string>(imageName || 'hero-custom');
  const [contentToAdapt, setContentToAdapt] = useState<string>('');
  const [adaptSlightly, setAdaptSlightly] = useState<boolean>(true);
  const [doFront, setDoFront] = useState<boolean>(false);
  const [doSome, setDoSome] = useState<boolean>(false);
  
  // Advanced operation states
  // const [sourceImage] = useState<string>(''); // Removed unused state
  
  // History states
  const [imageHistory, setImageHistory] = useState<HistoryEntry[]>([]);
  // const [previewImageUrl, setPreviewImageUrl] = useState<string>(''); // Removed unused state
  
  // Use the prompt management hook
  const {
    basePrompt,
    adaptedPrompt,
    finalPrompt,
    isAdapting,
    updateFinalPrompt,
  } = usePromptManagement({
    generationType,
    contentToAdapt,
    adaptSlightly,
    doFront,
    doSome,
  });
  
  // Determine if image is a hero or section image based on name
  const getImageType = (name: string): 'hero' | 'image' => {
    if (name.startsWith('hero-')) {
      return 'hero';
    } else if (name.startsWith('section-')) {
      return 'image';
    }
    // Default to hero if can't determine
    return 'hero';
  };
  
  // Initialize / React to imageName changes
  useEffect(() => {
    const imageType = getImageType(imageName);
    setGenerationType(imageType);
    // Get the defaults object, but we'll access do_some carefully below
    const defaults = DEFAULT_PARAMS[imageType]; 
    
    // Safely access do_some based on the known imageType
    let currentDoSome = false;
    if (imageType === 'image') {
      // Directly access the known ImageParams default object
      currentDoSome = DEFAULT_PARAMS.image.do_some; 
    } 
    // No need for an else, currentDoSome defaults to false
    setDoSome(currentDoSome);

    // Set other states using the general defaults object
    setContentToAdapt(defaults.content_to_adapt);
    setAdaptSlightly(defaults.adapt_slightly || false);
    setDoFront(defaults.do_front || false);
    
    setOutputFileName(imageName);
  }, [imageName]); 
  
  // Fetch metadata from API
  const fetchImageMetadata = async () => {
    if (!imageName) {
      console.log("[UnifiedDebugPanel] Skipping metadata fetch: imageName is empty.");
      // Reset states potentially set by metadata
      const defaults = DEFAULT_PARAMS[getImageType(imageName)]; // Use current type
      setContentToAdapt(defaults.content_to_adapt);
      setAdaptSlightly(defaults.adapt_slightly || false);
      setDoFront(defaults.do_front || false);
      
      // Safe approach for accessing do_some property
      if (getImageType(imageName) === 'image') {
        const imageParams = DEFAULT_PARAMS.image;
        setDoSome(imageParams.do_some);
      } else {
        setDoSome(false);
      }
      return;
    }
    console.log(`[UnifiedDebugPanel] Fetching metadata for: ${imageName}`);
    try {
      // Use the imported getParameters function
      const metadata = await getParameters(imageName); 
      console.log("[UnifiedDebugPanel] Fetched metadata from API:", metadata);
      updateMetadataState(metadata);
    } catch (error) {
      console.error('Error fetching image metadata:', error);
      // Optionally reset state on error
      // updateMetadataState({}); 
    }
  };
  
  // Update state with metadata
  const updateMetadataState = (metadata: ImageParameters | null) => {
    if (!metadata || Object.keys(metadata).length === 0) return; 

    setContentToAdapt(metadata.content_to_adapt ?? contentToAdapt);
    setAdaptSlightly(metadata.adapt_slightly ?? adaptSlightly);
    setDoFront(metadata.do_front ?? doFront);

    // Use the type property from metadata for a safe type guard
    if (metadata.type === 'image' && metadata.do_some !== undefined) {
      // Now TypeScript knows metadata is ImageParams (or similar) if type is 'image'
      // and we can safely access do_some
      setDoSome(metadata.do_some); 
    } else if (metadata.type === 'hero') {
       // Explicitly reset do_some if the fetched metadata is for a hero image
       setDoSome(false);
    }
  };
  
  // Fetch history for the current image
  const fetchImageHistory = async () => {
    if (!imageName) {
       console.log("[UnifiedDebugPanel] Skipping history fetch: imageName is empty.");
       setImageHistory([]);
       return;
    }
    console.log(`[UnifiedDebugPanel] Fetching history for: ${imageName}`);
    try {
      const historyData = await getImageHistory(imageName);
      setImageHistory(historyData || []);
    } catch (error) {
      console.error('Error fetching image history:', error);
      setImageHistory([]);
      toast({ title: 'Error', description: 'Failed to load image history', variant: 'destructive' });
    }
  };
  
  // Generate image function
  const handleGenerateImage = async () => {
    if (!outputFileName || isProcessing) {
      return;
    }
    console.log(`[UnifiedDebugPanel] handleGenerateImage called for: ${outputFileName}`);
    setIsProcessing(true);
    setError(null);
    setGenerationProgress(0);

    const isCustom = finalPrompt !== adaptedPrompt && finalPrompt !== basePrompt;

    // Construct parameters object matching ImageParameters type
    const generationParams: ImageParameters = {
      // Add potentially missing fields required by ImageParameters
      type: generationType, // Based on current state
      width: generationType === 'hero' ? 1920 : 800, // Example defaults
      height: generationType === 'hero' ? 1080 : 800, // Example defaults
      style: 'default', // Add sensible default or fetch from metadata
      theme: 'light', // Add sensible default or fetch from metadata
      
      // Fields from component state
      content_to_adapt: contentToAdapt,
      preferred_color: '', // Add input field if needed
      adapt_slightly: adaptSlightly,
      do_front: doFront,
      do_save: true,
      use_custom_prompt: isCustom,
      custom_prompt: isCustom ? finalPrompt : undefined, 
      prompts: {
        base_prompt: basePrompt,
        adapted_prompt: adaptedPrompt,
        final_prompt: finalPrompt
      },
      parameters: {}, // Base parameters object for API
      // Add do_some only if imageType is 'image'
      ...(generationType === 'image' && { do_some: doSome }),
    };

    // Clean up undefined optional fields (optional, but good practice)
    Object.keys(generationParams).forEach((key) => {
       const typedKey = key as keyof ImageParameters;
       if (generationParams[typedKey] === undefined) {
          delete generationParams[typedKey];
       }
    });

    try {
      // Determine the correct endpoint key based on generationType
      const endpointKey = generationType === 'hero' ? 'generateUmyndHero' : 'generateUmyndImage';
      
      const result = await generateImage( // Use imported function
        endpointKey,
        outputFileName, 
        generationParams // Pass the correctly typed object
      );
      
      toast({
        title: 'Generation Successful',
        description: `Generated ${outputFileName}. Image path: ${result.image_path}`,
      });
      simulateProgress(); 
      if (onRefresh) {
         setTimeout(onRefresh, 1000); 
      }
    } catch (error) {
      console.error('Error generating image:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError(`Failed to generate image: ${errorMessage}`);
      toast({
        title: 'Generation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
       setIsProcessing(false); 
       setGenerationProgress(0); 
    }
  };
  
  // Simulate progress (e.g., for API calls that don't have websockets)
  const simulateProgress = () => {
    setError(null);
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setGenerationProgress(Math.min(progress, 100));
      if (progress >= 100) {
        clearInterval(interval);
        setIsProcessing(false);
      }
    }, 300);
  };
  
  /*
  // Handle color transfer - Commented out as currently unused
  const handleColorTransfer = async () => {
    if (!sourceImage) {
      setError('Please select a source image first');
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const response = await fetch(`${serviceUrl}/adept-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_image: sourceImage,
          target_image: imageName,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Color transfer failed with status ${response.status}`);
      }
      
      // Get the result data
      await response.json();
      
      toast({
        title: 'Color Transfer Complete',
        description: `Applied colors from ${sourceImage} to ${imageName}`,
      });
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error in color transfer:', error);
      setError(`Color transfer failed: ${error instanceof Error ? error.message : String(error)}`);
      
      toast({
        title: 'Color Transfer Failed',
        description: `Failed to transfer colors to ${imageName}`,
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Handle brightness transfer - Commented out as currently unused
  const handleBrightnessTransfer = async () => {
    if (!brightnessSourceImage) {
      setError('Please select a source image first');
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const response = await fetch(`${serviceUrl}/adept-brightness`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_image: brightnessSourceImage,
          target_image: imageName,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Brightness transfer failed with status ${response.status}`);
      }
      
      // Get the result data
      await response.json();
      
      toast({
        title: 'Brightness Transfer Complete',
        description: `Applied brightness from ${brightnessSourceImage} to ${imageName}`,
      });
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error in brightness transfer:', error);
      setError(`Brightness transfer failed: ${error instanceof Error ? error.message : String(error)}`);
      
      toast({
        title: 'Brightness Transfer Failed',
        description: `Failed to transfer brightness to ${imageName}`,
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };
  */
  
  // Restore a specific version
  const handleRestoreVersion = async (timestamp: string) => {
    if (!imageName || isProcessing) return;
    console.log(`[UnifiedDebugPanel] Restoring version: ${timestamp} for ${imageName}`);
    setIsProcessing(true);
    setError(null);

    try {
      // const result = await restoreVersion(imageName, timestamp);
      toast({ title: 'Success', description: `Restored version ${timestamp}` });
      fetchImageHistory(); // Refresh history list
      onRefresh?.();
    } catch (error: unknown) { // Use unknown
      console.error('Error restoring version:', error);
      const message = error instanceof Error ? error.message : 'Failed to restore version';
      setError(message);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete a specific history version
  const handleDeleteVersion = async (timestamp: string) => {
    if (!imageName || isProcessing) return;
    console.log(`[UnifiedDebugPanel] Deleting version: ${timestamp} for ${imageName}`);
    setIsProcessing(true);
    setError(null);

    try {
      await deleteHistoryVersion(imageName, timestamp);
      toast({ title: 'Success', description: `Deleted version ${timestamp}` });
      fetchImageHistory(); // Refresh history list
      onRefresh?.();
    } catch (error: unknown) { // Use unknown
      console.error('Error deleting version:', error);
      const message = error instanceof Error ? error.message : 'Failed to delete version';
      setError(message);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle clicking on a history item to preview it
  const handlePreviewHistoryItem = (item: HistoryEntry) => { // Use HistoryEntry
    console.log("[UnifiedDebugPanel] Previewing history item:", item);
    // Use item.timestamp or a unique identifier from item if available
    if (item && item.timestamp) { 
      // Construct preview URL based on imageName and timestamp
      // Assuming history images might be stored or accessible via a pattern
      const imageUrl = `/history/${imageName}_${item.timestamp}.png?t=${Date.now()}`; // Example path
      console.log(`[UnifiedDebugPanel] Setting preview URL to: ${imageUrl}`);
      // Update metadata state based on the previewed item's *parameters*
      if (item.parameters) {
         updateMetadataState(item.parameters); // Pass item.parameters, not item.prompts
      }
    } else {
      console.warn("[UnifiedDebugPanel] Could not set preview URL: Invalid item data.");
    }
  };

  const handleImageError = () => {
    console.warn("Error loading image, setting placeholder.")
    // If current image URL fails, maybe set to a default placeholder?
    // setCurrentImageUrl('/placeholder.png'); // Example
  }
  
  // --- Display Section ---
  // Simple display of the target service URL
  const ServiceIndicator = () => (
    <div className="text-xs text-muted-foreground p-2 border-t mt-4">
      Target Service: {serviceUrl}
    </div>
  );
  
  return (
    <div className="flex flex-col h-full">
      {/* Status Text - Moved from DebugImageOverlay for better context */}
      <div className="flex items-center justify-end mb-4 text-xs">
         {/* TODO: Get status dynamically or pass as prop if needed here */}
         {/* Example placeholder - replace with actual logic if moved */}
         {/* <span className="inline-block w-2 h-2 rounded-full bg-success mr-1"></span> */}
         {/* <span className="text-foreground/80">API (http://localhost:8000)</span> */}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-grow flex flex-col">
        {/* Restore consistent tabs styling */}
        <TabsList className="grid w-full grid-cols-2 bg-muted p-1 rounded-full h-auto">
          <TabsTrigger value="generate">Generate/Edit</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* Generate/Edit Tab Content */}
        <TabsContent value="generate" className="flex-grow mt-4"> {/* Added mt-4 for spacing */}
          {/* Removed grid layout from here, will apply in DebugImageOverlay */}
          {/* Content of the Generate/Edit Tab */}
          <div className="space-y-4">
            {/* Output Filename */}
            <div>
              <Label htmlFor="outputFileName">Output Filename (.png)</Label>
              <Input 
                id="outputFileName" 
                value={outputFileName} 
                onChange={(e) => setOutputFileName(e.target.value)} 
                placeholder="e.g., hero-about-us"
              />
            </div>

            {/* Prompt Section */}
            <div className="border p-4 rounded-md space-y-3 bg-muted/20">
              <h4 className="font-medium text-sm mb-2">Prompt Management</h4>
              {/* Base Prompt (Readonly) */}
              <div>
                <Label htmlFor="basePrompt" className="text-xs text-muted-foreground">Base Prompt</Label>
                <p id="basePrompt" className="text-xs p-2 bg-background rounded border h-16 overflow-y-auto">
                  {basePrompt}
                </p>
              </div>
              
              {/* Content to Adapt */}
              <div>
                <Label htmlFor="contentToAdapt">Content to Adapt Prompt</Label>
                <Input 
                  id="contentToAdapt" 
                  value={contentToAdapt} 
                  onChange={(e) => setContentToAdapt(e.target.value)}
                  placeholder="Optional context for adaptation"
                />
              </div>

              {/* Adaptation Flags */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="adaptSlightly" 
                    checked={adaptSlightly} 
                    onCheckedChange={(checked) => setAdaptSlightly(!!checked)} 
                  />
                  <Label htmlFor="adaptSlightly" className="text-sm font-normal">Adapt Slightly</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="doFront" 
                    checked={doFront} 
                    onCheckedChange={(checked) => setDoFront(!!checked)} 
                  />
                  <Label htmlFor="doFront" className="text-sm font-normal">Front Perspective</Label>
                </div>
                {/* Only show 'Do Some' checkbox if generationType is 'image' */}
                {generationType === 'image' && (
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="doSome" 
                      checked={doSome} 
                      onCheckedChange={(checked) => setDoSome(!!checked)} 
                    />
                    <Label htmlFor="doSome" className="text-sm font-normal">More Variance</Label>
                  </div>
                )}
              </div>

              {/* Adapted Prompt (Readonly) */}
              <div>
                <Label htmlFor="adaptedPrompt" className="text-xs text-muted-foreground">Adapted Prompt</Label>
                <p id="adaptedPrompt" className="text-xs p-2 bg-background rounded border h-16 overflow-y-auto">
                  {isAdapting ? 'Adapting...' : adaptedPrompt}
                </p>
              </div>

              {/* Final Prompt (Editable) */}
              <div>
                <Label htmlFor="finalPrompt">Final Prompt (Editable)</Label>
                <textarea 
                  id="finalPrompt" 
                  value={finalPrompt} 
                  onChange={(e) => updateFinalPrompt(e.target.value)}
                  className="w-full p-2 border rounded min-h-[80px] text-sm bg-background"
                  placeholder="Final prompt used for generation"
                />
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button onClick={handleGenerateImage} disabled={isProcessing || !outputFileName}>
                {isProcessing ? `Generating... (${generationProgress}%)` : 'Generate Image'}
              </Button>
               {/* Metadata */}
              <Button onClick={fetchImageMetadata} variant="secondary" size="sm">Load Metadata</Button>
              
               {/* Advanced Color/Brightness (Placeholder Buttons) */}
              {/* 
              <Button onClick={handleColorTransfer} disabled={isProcessing} variant="outline" size="sm">
                Transfer Color
              </Button>
              <Button onClick={handleBrightnessTransfer} disabled={isProcessing} variant="outline" size="sm">
                Transfer Brightness
              </Button>
               */}
            </div>
             {error && <p className="text-destructive text-sm mt-2">{error}</p>}
          </div>
        </TabsContent>

        {/* History Tab Content */}
        <TabsContent value="history" className="flex-grow mt-4"> {/* Added mt-4 for spacing */}
          <div className="flex flex-col h-full">
             <Button onClick={fetchImageHistory} disabled={isProcessing} size="sm" className="mb-4 self-start">Refresh History</Button>
             {imageHistory.length === 0 ? (
               <p className="text-muted-foreground text-sm">No history available for this image.</p>
             ) : (
               <div className="flex-grow overflow-y-auto border rounded-md">
                 <ul className="divide-y">
                   {imageHistory.map((item) => (
                     <li key={item.timestamp} className="p-3 flex items-center justify-between hover:bg-muted/50">
                       <div className="flex items-center gap-3">
                         <div className="relative w-12 h-12 flex-shrink-0 bg-muted rounded">
                           <Image 
                             src={`/${imageName}.png?t=${item.timestamp}`} 
                             alt={`History ${item.timestamp}`} 
                             fill
                             className="object-contain rounded"
                             sizes="48px"
                             onError={handleImageError} // Add error handling
                           />
                         </div>
                         <div className="text-sm">
                           <button 
                             onClick={() => handlePreviewHistoryItem(item)} 
                             className="font-medium hover:underline text-left"
                             title={item.prompts?.final_prompt ?? 'View parameters'}
                           >
                             {new Date(item.timestamp).toLocaleString()}
                           </button>
                           <p className="text-xs text-muted-foreground truncate w-64" title={item.prompts?.final_prompt ?? ''}>
                             {item.prompts?.final_prompt ?? 'No prompt info'}
                           </p>
                         </div>
                       </div>
                       <div className="flex items-center gap-2">
                         <Button 
                           variant="secondary" 
                           size="sm" 
                           onClick={() => handleRestoreVersion(item.timestamp)}
                           disabled={isProcessing}
                         >
                           Restore
                         </Button>
                         <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteVersion(item.timestamp)}
                            disabled={isProcessing}
                            className="text-destructive hover:bg-destructive/10 p-2"
                          >
                            <Trash2 className="w-4 h-4" />
                         </Button>
                       </div>
                     </li>
                   ))}
                 </ul>
               </div>
             )}
          </div>
        </TabsContent>
      </Tabs>
      <ServiceIndicator /> {/* Add the indicator at the bottom */}
    </div>
  );
}